import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/SimpleToast";
import { Settings as SettingsIcon, Mail, FileText, Database, Eraser, Sparkles, Loader2, Code, Shield, Bell, PenLine, Smartphone } from "lucide-react";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { ExchangeSettings } from "@/components/settings/ExchangeSettings";
import { LexwareSettings } from "@/components/settings/LexwareSettings";
import { SmtpSettings } from "@/components/settings/SmtpSettings";
import { TwoFactorSettings } from "@/components/settings/TwoFactorSettings";
import { PushNotificationSettings } from "@/components/settings/PushNotificationSettings";
import { MySignatureSettings } from "@/components/settings/MySignatureSettings";
import { AppInstallSettings } from "@/components/settings/AppInstallSettings";
import { ScrollFade } from "@/components/ScrollFade";
import { useState } from "react";
import { customersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [cleaningHighlights, setCleaningHighlights] = useState(false);
  const [cleaningTags, setCleaningTags] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleCleanupHighlights = async () => {
    setCleaningHighlights(true);
    try {
      const response = await customersApi.cleanupHighlights();
      const result = response.data;
      toast.success(
        `Bereinigung abgeschlossen: ${result.cleaned} Notizen bereinigt, ${result.auto_linked} Links erstellt, ${result.unchanged} unverändert`
      );
    } catch (error) {
      toast.error("Fehler bei der Bereinigung");
    } finally {
      setCleaningHighlights(false);
    }
  };

  const handleCleanupTags = async () => {
    setCleaningTags(true);
    try {
      const response = await customersApi.cleanupNotes();
      const result = response.data;
      toast.success(
        `Tag-Bereinigung abgeschlossen: ${result.cleaned} Notizen bereinigt, ${result.unchanged} unverändert`
      );
    } catch (error) {
      toast.error("Fehler bei der Tag-Bereinigung");
    } finally {
      setCleaningTags(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <SettingsIcon className="w-8 h-8" />
          Einstellungen
        </h1>
        <p className="text-muted-foreground mt-1">
          Konfigurieren Sie E-Mail, Exchange, Lexware und Erinnerungen
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="lexware" className="space-y-6">
        <ScrollFade>
        <TabsList className="w-max max-w-none overflow-x-visible">
          <TabsTrigger value="lexware" className="gap-2">
            <FileText className="w-4 h-4" />
            Lexware
          </TabsTrigger>
          <TabsTrigger value="exchange" className="gap-2" data-testid="tab-exchange">
            <Mail className="w-4 h-4" />
            Microsoft 365
          </TabsTrigger>
          <TabsTrigger value="smtp" className="gap-2">
            <Mail className="w-4 h-4" />
            SMTP
          </TabsTrigger>
          <TabsTrigger value="sicherheit" className="gap-2" data-testid="tab-sicherheit">
            <Shield className="w-4 h-4" />
            Sicherheit
          </TabsTrigger>
          <TabsTrigger value="benachrichtigungen" className="gap-2" data-testid="tab-benachrichtigungen">
            <Bell className="w-4 h-4" />
            Benachrichtigungen
          </TabsTrigger>
          <TabsTrigger value="signature" className="gap-2" data-testid="tab-signature">
            <PenLine className="w-4 h-4" />
            Unterschrift
          </TabsTrigger>
          {isAdmin && (
          <TabsTrigger value="datenpflege" className="gap-2" data-testid="tab-datenpflege">
            <Database className="w-4 h-4" />
            Datenpflege
          </TabsTrigger>
          )}
          <TabsTrigger value="app" className="gap-2" data-testid="tab-app">
            <Smartphone className="w-4 h-4" />
            App
          </TabsTrigger>
        </TabsList>
        </ScrollFade>

        {/* Lexware Tab */}
        <TabsContent value="lexware">
          <LexwareSettings toast={toast} />
        </TabsContent>

        {/* Exchange Tab */}
        <TabsContent value="exchange">
          <ExchangeSettings toast={toast} />
        </TabsContent>

        {/* SMTP Tab */}
        <TabsContent value="smtp">
          <SmtpSettings toast={toast} />
        </TabsContent>

        {/* Sicherheit Tab */}
        <TabsContent value="sicherheit">
          <TwoFactorSettings toast={toast} />
        </TabsContent>

        {/* Benachrichtigungen Tab */}
        <TabsContent value="benachrichtigungen">
          <PushNotificationSettings toast={toast} />
        </TabsContent>

        {/* Unterschrift Tab */}
        <TabsContent value="signature">
          <MySignatureSettings />
        </TabsContent>

        {/* Datenpflege Tab */}
        <TabsContent value="datenpflege">
          {isAdmin && (<>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eraser className="w-5 h-5" />
                Notizen bereinigen
              </CardTitle>
              <CardDescription>
                Bereinigt alle Kundennotizen von blauen Markierungen und erstellt automatisch Links für URLs und E-Mail-Adressen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                <p className="font-medium">Diese Funktion führt folgende Aktionen durch:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Entfernt blaue Hintergrund-Markierungen aus allen Notizen</li>
                  <li>Wandelt URLs automatisch in klickbare Links um</li>
                  <li>Wandelt E-Mail-Adressen in mailto-Links um</li>
                </ul>
              </div>
              <Button 
                onClick={handleCleanupHighlights} 
                disabled={cleaningHighlights}
                className="gap-2"
                data-testid="cleanup-highlights-btn"
              >
                {cleaningHighlights ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Bereinige...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Alle Notizen bereinigen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* BBCode/HTML Tag Cleanup Card */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                BBCode & HTML Tags entfernen
              </CardTitle>
              <CardDescription>
                Entfernt alle [p], [url], &lt;p&gt;, &lt;br&gt; und andere Tags aus den Kundennotizen und hinterlässt sauberen Text.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                <p className="font-medium">Diese Funktion führt folgende Aktionen durch:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Entfernt [p], [/p], [url], [b], [i] und andere BBCode-Tags</li>
                  <li>Entfernt &lt;p&gt;, &lt;br&gt;, &lt;span&gt; und andere HTML-Tags</li>
                  <li>Behält E-Mail-Adressen und URLs als Text bei</li>
                  <li>Konvertiert &amp;amp; &amp;nbsp; etc. zu normalem Text</li>
                </ul>
              </div>
              <Button 
                onClick={handleCleanupTags} 
                disabled={cleaningTags}
                className="gap-2"
                variant="outline"
                data-testid="cleanup-tags-btn"
              >
                {cleaningTags ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Bereinige Tags...
                  </>
                ) : (
                  <>
                    <Code className="w-4 h-4" />
                    BBCode/HTML Tags entfernen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
          </>)}
        </TabsContent>

        {/* App Tab */}
        <TabsContent value="app">
          <AppInstallSettings />
        </TabsContent>
      </Tabs>

      {/* Neuerungen / Onboarding erneut anzeigen */}
      <Card data-testid="onboarding-recap-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Neuerungen
          </CardTitle>
          <CardDescription>
            Sehen Sie sich die Einführung zu den Multi-User-Funktionen (Rollen, Zuständigkeiten, Kommentare & @Erwähnungen, Änderungsverlauf) jederzeit erneut an.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowOnboarding(true)}
            data-testid="show-onboarding-btn"
          >
            <Sparkles className="w-4 h-4" />
            Neuerungen ansehen
          </Button>
        </CardContent>
      </Card>

      <OnboardingDialog forceOpen={showOnboarding} onClose={() => setShowOnboarding(false)} />

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Hilfe zur Konfiguration</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-3">
          <p>
            <strong>Microsoft Exchange / Office 365:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>SMTP Server: <code className="bg-muted px-1 rounded">smtp.office365.com</code></li>
            <li>Port: <code className="bg-muted px-1 rounded">587</code></li>
            <li>Benutzername: Ihre vollständige E-Mail-Adresse</li>
          </ul>
          <p className="mt-4">
            <strong>On-Premise Exchange:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>SMTP Server: Ihr Exchange-Server (z.B. <code className="bg-muted px-1 rounded">mail.r-network.de</code>)</li>
            <li>Port: <code className="bg-muted px-1 rounded">587</code> oder <code className="bg-muted px-1 rounded">25</code></li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
